import interLatin from '@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?inline'
import { INITIAL_PROJECT_INFORMATION } from 'app-constants'
import { adaptColourToTheme, adaptHeaderColoursToTheme, contrastRatio, parseHex, rgbToHsl } from 'helpers/colour'
import { ResolvedTheme } from 'helpers/theme'
import { IProject } from 'types'

/** The hub's colours and the app's neutrals, as a page gets them: CSS variables on :root and morta.theme. */
export interface PageTheme {
  /** The app's light or dark theme, as the reader has it. The page reloads when it changes. */
  mode: ResolvedTheme
  primary: string
  onPrimary: string
  headerBackground: string
  headerText: string
  text: string
  mutedText: string
  surface: string
  sunkenSurface: string
  border: string
  success: string
  danger: string
  warning: string
  info: string
  /** Buttons are solid and neutral, as in the app: black on light, white on dark, never the hub's colour. */
  buttonBackground: string
  buttonText: string
  /** The app's corners: cards, panels and dialogs; and buttons, inputs and selects. */
  radius: string
  radiusSmall: string
  /** Surfaces that float (menus, tooltips), form fields, loading placeholders, and text on a solid fill. */
  raisedSurface: string
  fieldSurface: string
  skeleton: string
  onSolid: string
  /** Something floating over the page: a tooltip or popover. */
  popoverSurface: string
  popoverBorder: string
  popoverShadow: string
  /** Charts: grid lines, axes, reference lines, de-emphasised marks, and good / warning / critical. */
  chartGrid: string
  chartAxis: string
  chartReference: string
  chartMuted: string
  good: string
  caution: string
  critical: string
  /** Eight series colours for charts, the hub's own colour first, the rest well apart from it. */
  series: string[]
  font: string
}

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i
// The app's font, as style.scss names it. The page can't fetch fonts (its CSP), so Inter comes inside it.
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

// Inter's Latin subset, every weight in one 48KB variable file, as a data: URL: nothing is downloaded, and
// the page stays sealed. Characters outside the subset fall back to the system font.
const INTER_FACE = `@font-face { font-family: 'Inter'; font-style: normal; font-weight: 100 900; font-display: block; src: url(${interLatin}) format('woff2'); unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD; }`

// Series colours after the hub's own: a qualitative set that reads on light and dark surfaces.
const SERIES_BASE = [
  '#2e7ff9',
  '#14b8a6',
  '#8b5cf6',
  '#f97316',
  '#ec4899',
  '#22c55e',
  '#eab308',
  '#64748b',
  '#06b6d4',
  '#ef4444'
]
const SERIES_COUNT = 8
// Hues closer than this to the hub's colour would read as the same series.
const SERIES_HUE_GAP = 30

const hueOf = (hex: string): number | null => {
  const rgb = parseHex(hex)
  if (!rgb) return null
  const [hue, saturation] = rgbToHsl(rgb)
  // A grey has no hue to clash with.
  return saturation < 0.15 ? null : hue
}

/**
 * The chart series: the hub's colour first, lifted for the dark theme if it would disappear against the
 * surface, then the base set without colours too close in hue to it.
 */
export function seriesColours(primary: string, surface: string, mode: ResolvedTheme): string[] {
  const first = contrastRatio(primary, surface) >= 3 ? primary : adaptColourToTheme(primary, mode)
  const firstHue = hueOf(first)
  const apart = SERIES_BASE.filter((colour) => {
    const hue = hueOf(colour)
    if (firstHue === null || hue === null) return true
    const gap = Math.abs(hue - firstHue) % 360
    return Math.min(gap, 360 - gap) >= SERIES_HUE_GAP
  })
  return [first, ...apart].slice(0, SERIES_COUNT)
}

/** A colour for the page's stylesheet: only hex values, so hub settings can never inject CSS. */
export const hexOr = (value: unknown, fallback: string): string =>
  typeof value === 'string' && HEX.test(value.trim()) ? value.trim() : fallback

// The app's own design tokens, read from its stylesheet so the page follows them in light and dark; the
// fallbacks are their light values.
const token = (name: string, fallback: string) =>
  hexOr(getComputedStyle(document.documentElement).getPropertyValue(name), fallback)

// A colour the app paints with, which may be one of its tokens (var(--color-…)), as a hex the page can use.
const paint = (value: string, fallback: string): string => {
  const variable = /^var\((--[\w-]+)\)$/.exec(value.trim())
  return variable ? token(variable[1], fallback) : hexOr(value, fallback)
}

/**
 * A column header's colours as the grid paints them in the reader's theme: default headers take the theme's
 * surface and text, and custom ones are adapted for dark like any other fill.
 */
export function pageHeader(background: unknown, text: unknown, mode: ResolvedTheme) {
  const stored = (value: unknown) => (typeof value === 'string' && HEX.test(value.trim()) ? value.trim() : null)
  const adapted = adaptHeaderColoursToTheme(stored(background), stored(text), mode)
  return { background: paint(adapted.background, '#f5f5f5'), text: paint(adapted.text, '#333333') }
}

type HubColours = Pick<
  IProject,
  'primaryColour' | 'fontColour' | 'defaultHeaderBackgroundColor' | 'defaultHeaderTextColor'
>

export function pageTheme(hub?: Partial<HubColours> | null, mode: ResolvedTheme = 'light'): PageTheme {
  const defaults = INITIAL_PROJECT_INFORMATION
  const header = pageHeader(
    hexOr(hub?.defaultHeaderBackgroundColor, defaults.defaultHeaderBackgroundColor),
    hexOr(hub?.defaultHeaderTextColor, defaults.defaultHeaderTextColor),
    mode
  )
  return {
    mode,
    // The hub's brand colour stays as it is in both themes, as on the app's own primary buttons.
    primary: hexOr(hub?.primaryColour, defaults.primaryColour),
    onPrimary: hexOr(hub?.fontColour, defaults.fontColour),
    headerBackground: header.background,
    headerText: header.text,
    text: token('--color-body', '#333333'),
    mutedText: token('--color-muted', '#707070'),
    surface: token('--color-surface', '#ffffff'),
    sunkenSurface: token('--color-surface-sunken', '#f5f5f5'),
    border: token('--color-divider', '#d9dbde'),
    success: token('--color-green', '#59cd90'),
    danger: token('--color-red', '#f72c60'),
    warning: token('--color-alert', '#f1c744'),
    info: token('--color-blue', '#2e7ff9'),
    buttonBackground: token('--color-surface-inverse', '#333333'),
    buttonText: token('--color-surface', '#ffffff'),
    radius: '8px',
    radiusSmall: '6px',
    raisedSurface: token('--color-surface-raised', '#ffffff'),
    // The dark theme's field is a color-mix(), which a page can't take: it falls back to the surface there.
    fieldSurface: token('--color-field', token('--color-surface', '#ffffff')),
    skeleton: token('--color-skeleton', '#dddbdd'),
    onSolid: token('--color-on-solid', '#ffffff'),
    popoverSurface: token('--color-surface-raised', '#ffffff'),
    popoverBorder: token('--color-divider', '#d9dbde'),
    popoverShadow:
      mode === 'dark'
        ? '0 4px 12px -2px rgb(0 0 0 / 0.5), 0 2px 4px -2px rgb(0 0 0 / 0.4)'
        : '0 4px 12px -2px rgb(0 0 0 / 0.12), 0 2px 4px -2px rgb(0 0 0 / 0.08)',
    chartGrid: token('--color-surface-hover', '#f0f1f3'),
    chartAxis: token('--color-divider', '#d9dbde'),
    chartReference: token('--color-faint', '#b9b9b9'),
    chartMuted: token('--color-surface-strong', '#d9dbde'),
    good: token('--color-green', '#59cd90'),
    caution: token('--color-alert', '#f1c744'),
    critical: token('--color-red', '#f72c60'),
    series: seriesColours(hexOr(hub?.primaryColour, defaults.primaryColour), token('--color-surface', '#ffffff'), mode),
    font: FONT
  }
}

/**
 * The sizes every page shares, as CSS variables only (they don't change with the hub or the theme): the
 * app's type scale, spacing from xs to xl for gaps and padding, and its motion.
 */
const SCALE_CSS = [
  '--morta-text-caption: 12px; --morta-leading-caption: 16px;',
  '--morta-text-body: 13px; --morta-leading-body: 20px;',
  '--morta-text-heading: 15px; --morta-leading-heading: 22px;',
  '--morta-text-title: 20px; --morta-leading-title: 28px;',
  '--morta-gap-xs: 4px; --morta-gap-sm: 8px; --morta-gap-md: 12px; --morta-gap-lg: 16px; --morta-gap-xl: 24px;',
  '--morta-pad-xs: 4px; --morta-pad-sm: 8px; --morta-pad-md: 12px; --morta-pad-lg: 16px; --morta-pad-xl: 24px;',
  '--morta-duration-fast: 120ms; --morta-duration-base: 200ms;'
].join(' ')

/** CSS variable names, e.g. --morta-primary, --morta-on-primary, --morta-header-bg. */
const THEME_VARIABLES: Record<Exclude<keyof PageTheme, 'mode' | 'series'>, string> = {
  primary: '--morta-primary',
  onPrimary: '--morta-on-primary',
  headerBackground: '--morta-header-bg',
  headerText: '--morta-header-text',
  text: '--morta-text',
  mutedText: '--morta-muted',
  surface: '--morta-surface',
  sunkenSurface: '--morta-surface-sunken',
  border: '--morta-border',
  success: '--morta-success',
  danger: '--morta-danger',
  warning: '--morta-warning',
  info: '--morta-info',
  buttonBackground: '--morta-button-bg',
  buttonText: '--morta-button-text',
  radius: '--morta-radius',
  radiusSmall: '--morta-radius-sm',
  raisedSurface: '--morta-surface-raised',
  fieldSurface: '--morta-field',
  skeleton: '--morta-skeleton',
  onSolid: '--morta-on-solid',
  popoverSurface: '--morta-popover-bg',
  popoverBorder: '--morta-popover-border',
  popoverShadow: '--morta-popover-shadow',
  chartGrid: '--morta-chart-grid',
  chartAxis: '--morta-chart-axis',
  chartReference: '--morta-chart-reference',
  chartMuted: '--morta-chart-muted',
  good: '--morta-good',
  caution: '--morta-caution',
  critical: '--morta-critical',
  font: '--morta-font'
}

/**
 * The page's theme stylesheet. Put before the page's own styles so a page can still override a variable.
 * color-scheme makes the browser draw scrollbars and form controls in the same theme.
 */
export function themeCss(theme: PageTheme): string {
  const variables = (Object.keys(THEME_VARIABLES) as Array<keyof typeof THEME_VARIABLES>)
    .map((key) => `${THEME_VARIABLES[key]}: ${theme[key]};`)
    .join(' ')
  const series = theme.series.map((colour, index) => `--morta-series-${index + 1}: ${colour};`).join(' ')
  return `${INTER_FACE} :root { color-scheme: ${theme.mode}; ${variables} ${series} ${SCALE_CSS} }`
}
